import { useState, useEffect, useRef, useCallback } from "react";
import IMG_LOGO from './assets/logo.jpg';
import DocumentacionTab from './DocumentacionTab';

/* ══════════════════════════════════════════════════════════════
   FONT INJECTION
══════════════════════════════════════════════════════════════ */
const fontLink = document.createElement("link");
fontLink.href = "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap";
fontLink.rel = "stylesheet";
document.head.appendChild(fontLink);

/* ══════════════════════════════════════════════════════════════
   GLOBAL STYLES
══════════════════════════════════════════════════════════════ */
const injectStyles = () => {
  const style = document.createElement("style");
  style.textContent = `
    @keyframes fadeSlideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
    @keyframes fadeSlideRight { from{opacity:0;transform:translateX(-20px)} to{opacity:1;transform:translateX(0)} }
    @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
    @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
    @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
    @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes scaleIn { from{transform:scale(0.9);opacity:0} to{transform:scale(1);opacity:1} }
    @keyframes progressFill { from{width:0%} to{width:var(--target-width)} }
    
    .lms-root { font-family:'IBM Plex Sans',sans-serif; }
    .lms-title { font-family:'Barlow Condensed',sans-serif; }
    .animate-fadeUp { animation: fadeSlideUp 0.5s ease forwards; }
    .animate-fadeRight { animation: fadeSlideRight 0.4s ease forwards; }
    .animate-scale { animation: scaleIn 0.3s ease forwards; }
    .animate-pulse { animation: pulse 2s infinite; }
    .animate-spin { animation: spin 1s linear infinite; }
    
    .shimmer-btn {
      background: linear-gradient(90deg, #003366 0%, #0055a5 50%, #003366 100%);
      background-size: 200%;
      transition: background-position 0.3s;
    }
    .shimmer-btn:hover { background-position: right center; }
    
    .glass-card {
      background: #ffffff;
      border: 1px solid rgba(0,51,102,0.12);
      box-shadow: 0 2px 12px rgba(0,51,102,0.08);
    }
    .nav-item:hover { background: rgba(0,85,165,0.12); }
    .option-btn { transition: all 0.2s; }
    .option-btn:hover:not(:disabled) { border-color: #0055a5; background: rgba(0,85,165,0.06); transform: translateX(4px); }
    .option-btn.selected { border-color: #0055a5; background: rgba(0,85,165,0.1); }
    .option-btn.correct { border-color: #15803d; background: rgba(21,128,61,0.08); }
    .option-btn.incorrect { border-color: #b91c1c; background: rgba(185,28,28,0.08); }
    
    .slide-card { transition: all 0.4s cubic-bezier(0.4,0,0.2,1); }
    .progress-bar-fill { transition: width 0.8s cubic-bezier(0.4,0,0.2,1); }
    
    .module-card { transition: all 0.25s; cursor:pointer; }
    .module-card:hover { transform: translateY(-2px); border-color: rgba(0,85,165,0.5) !important; box-shadow: 0 4px 20px rgba(0,51,102,0.1); }
    
    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: rgba(0,51,102,0.04); }
    ::-webkit-scrollbar-thumb { background: rgba(0,85,165,0.4); border-radius: 2px; }
    
    .ai-bubble-user { background: linear-gradient(135deg, #003366, #0055a5); }
    .ai-bubble-bot { background: rgba(0,85,165,0.08); border: 1px solid rgba(0,85,165,0.2); }
    
    .cert-bg {
      background: linear-gradient(135deg, #f0f6ff 0%, #dce8f7 40%, #f0f6ff 100%);
      border: 2px solid;
      border-image: linear-gradient(135deg, #92400e, #0055a5, #92400e) 1;
    }
    
    @media print {
      .no-print { display:none !important; }
      .cert-print { page-break-inside: avoid; }
    }
    
    .typing-dot { animation: blink 1s infinite; }
    .typing-dot:nth-child(2) { animation-delay: 0.2s; }
    .typing-dot:nth-child(3) { animation-delay: 0.4s; }
  `;
  document.head.appendChild(style);
};
injectStyles();

/* ══════════════════════════════════════════════════════════════
   TRAINING DATA
══════════════════════════════════════════════════════════════ */
const MODULES = [
  {
    id: "mod1", icon: "🛡️", color: "#0055a5",
    title: "Los Dos Pilares del Escudo",
    subtitle: "SAGRILAFT y PTEE — Fundamentos",
    duration: 15,
    description: "Comprende los dos sistemas de protección de RLpharma y su marco normativo obligatorio.",
    slides: [
      {
        title: "Nuestro Compromiso Inquebrantable",
        content: "En RLpharma S.A.S., la integridad no es negociable. Nuestro apetito de riesgo frente al Lavado de Activos, Financiación del Terrorismo, Proliferación de Armas (LA/FT/FPADM) y la Corrupción es CERO. El propósito del Escudo: proteger la reputación, la estabilidad financiera y el futuro de todos los que conformamos nuestra cadena de valor en el sector salud.",
        keyPoints: ["Apetito de riesgo: CERO — tolerancia absoluta", "Principios: Legalidad · Buena Fe · Transparencia · Debida Diligencia", "Afecta a TODOS los colaboradores sin excepción"]
      },
      {
        title: "SAGRILAFT: La Puerta de Entrada",
        content: "El SAGRILAFT (Sistema de Autocontrol y Gestión del Riesgo de LA/FT/FPADM) previene que dinero sucio o activos ilícitos ENTREN a nuestra operación. Combate el Lavado de Activos, la Financiación del Terrorismo y la Proliferación de Armas de Destrucción Masiva. Las autoridades principales son la UIAF y la Superintendencia de Sociedades.",
        keyPoints: ["Riesgo central: contagio criminal y fraude financiero", "Autoridad: UIAF + Superintendencia de Sociedades", "Herramienta clave: reporte ROS/AROS a la UIAF"]
      },
      {
        title: "PTEE: La Puerta de Salida",
        content: "El PTEE (Programa de Transparencia y Ética Empresarial) previene que malas prácticas, sobornos o pagos de facilitación SALGAN o sucedan DENTRO de RLpharma. Su objetivo es prevenir, detectar y sancionar actos de corrupción, soborno nacional y transnacional, y falta de ética empresarial.",
        keyPoints: ["Riesgo central: tráfico de influencias, cohecho, facilitación", "Autoridad: Superintendencia de Sociedades y entes de control", "Aplica tanto interna como externamente (soborno transnacional)"]
      },
      {
        title: "El Ecosistema Normativo",
        content: "RLpharma opera dentro de un robusto marco legal de tres capas. Estándares Internacionales: 40 Recomendaciones GAFI, ISO 37001, Convención de Mérida (ONU). Leyes Nacionales: Ley 526 (UIAF), Ley 1474 (Estatuto Anticorrupción), Ley 2195 (Transparencia y RUB). Normativa Sectorial: Circulares Supersociedades 100-000003, 100-000011, 100-000016; Decreto 780 Supersalud.",
        keyPoints: ["Ley 526 — Crea la UIAF y obliga reportes de operaciones sospechosas", "Ley 1474 — Estatuto Anticorrupción: sanciones penales graves", "Circular 100-000003 — Obliga el SAGRILAFT a empresas como RLpharma"]
      }
    ],
    quiz: [
      { id: "q1_1", text: "¿Cuál es el apetito de riesgo declarado por RLpharma frente al LA/FT y la Corrupción?", options: ["Moderado, aceptando riesgos calculados", "Alto para maximizar ganancias en nuevos mercados", "CERO — tolerancia absoluta a conductas de exposición significativa", "Bajo, con revisión trimestral"], correct: 2, explanation: "RLpharma declara apetito de riesgo CERO. La integridad no es negociable y no se tolerarán conductas u operaciones con exposición significativa." },
      { id: "q1_2", text: "La principal diferencia entre SAGRILAFT y PTEE es:", options: ["El SAGRILAFT aplica solo a gerencia y el PTEE a todos", "SAGRILAFT previene que dinero ilícito ENTRE; PTEE previene que sobornos SALGAN o sucedan internamente", "Son exactamente lo mismo, solo tienen nombres diferentes", "El PTEE solo aplica en contratos con el Estado"], correct: 1, explanation: "El SAGRILAFT es 'la puerta de entrada' (blinda contra dinero sucio externo) y el PTEE es 'la puerta de salida' (previene corrupción que sale o sucede internamente)." },
      { id: "q1_3", text: "¿Cuál ley colombiana creó la UIAF (Unidad de Información y Análisis Financiero)?", options: ["Ley 1474 — Estatuto Anticorrupción", "Ley 2195 — Transparencia", "Ley 526", "Decreto 780"], correct: 2, explanation: "La Ley 526 creó la UIAF, la entidad que recibe los reportes ROS (operaciones sospechosas) y AROS (ausencia de reportes sospechosos)." }
    ]
  },
  {
    id: "mod2", icon: "🏛️", color: "#1e40af",
    title: "Gobernanza: Guardianes de la Integridad",
    subtitle: "Roles, Responsabilidades y el Oficial de Cumplimiento",
    duration: 12,
    description: "Conoce el modelo de gobernanza de RLpharma y entiende el papel de cada guardián del sistema.",
    slides: [
      {
        title: "La Cadena de Responsabilidad",
        content: "La seguridad es una cadena donde cada eslabón tiene una función indelegable. El modelo tiene 4 niveles: Cúspide (Junta de Socios & Gerencia General), Control (Oficial de Cumplimiento), Ejecución (Gestión Humana, Compras, Comercial) y Línea de Defensa (todos los empleados y contratistas).",
        keyPoints: ["Junta de Socios: aprueba manuales, fija el tono, asume responsabilidad máxima", "Oficial de Cumplimiento: diseña, monitorea y reporta a UIAF (ROS/AROS)", "Empleados: primera línea — conocen, aplican y reportan"]
      },
      {
        title: "Rogelio: Tu Aliado Estratégico",
        content: "El Oficial de Cumplimiento no es un obstáculo comercial; es el estratega de nuestras defensas. Actúa con total independencia, reporta directamente a la Junta de Socios para garantizar imparcialidad, administra el Canal Ético y mantiene la Matriz de Riesgos calibrada frente a nuevas amenazas. Su objetivo es que los negocios se hagan, pero de forma segura.",
        keyPoints: ["Correo: oficialcumplimiento@rlpharma.com.co", "WhatsApp / Ext. 1007", "Independencia total garantizada — no reporta a línea comercial"]
      },
      {
        title: "Integración Operativa: Un Solo Motor",
        content: "El cumplimiento no duplica el trabajo; lo centraliza y lo hace eficiente. Un Solo Formato (matriz única de vinculación de terceros con datos legales + éticos), Un Solo Guardián (el mismo Oficial lidera ambos frentes SAGRILAFT y PTEE), Un Solo Canal (reportes éticos y de LA/FT convergen en la misma tubería), Un Solo Reporte (informe conjunto anual ante el Máximo Órgano Social).",
        keyPoints: ["Formulario único de vinculación de terceros", "Canal Ético único para alertas de LA/FT y corrupción", "Informe anual consolidado SAGRILAFT + PTEE a la Junta de Socios"]
      }
    ],
    quiz: [
      { id: "q2_1", text: "¿Quién es el máximo responsable de aprobar el Sistema SAGRILAFT y designar al Oficial de Cumplimiento?", options: ["El Revisor Fiscal", "La Gerencia General / Junta de Socios", "El área de Recursos Humanos", "El propio Oficial de Cumplimiento"], correct: 1, explanation: "Según la Circular 100-000003, es la Junta de Socios quien aprueba y designa. El Revisor Fiscal audita el sistema pero no lo aprueba ni designa." },
      { id: "q2_2", text: "¿Por qué el Oficial de Cumplimiento reporta directamente a la Junta de Socios y NO a la Gerencia Comercial?", options: ["Por tradición corporativa", "Para garantizar su independencia e imparcialidad en sus decisiones", "Porque es un requisito de auditoría externa solamente", "Por razones de presupuesto"], correct: 1, explanation: "La independencia total del Oficial de Cumplimiento es fundamental. Si reportara a líneas comerciales, podría haber presión para omitir alertas o aprobar negocios riesgosos." },
      { id: "q2_3", text: "El Canal Ético de RLpharma se puede contactar por:", options: ["Solo presencialmente en la oficina principal", "Correo oficialcumplimiento@rlpharma.com.co / WhatsApp Ext. 1007", "Solo mediante carta física registrada", "A través del jefe directo únicamente"], correct: 1, explanation: "El Canal Ético es accesible por correo electrónico (oficialcumplimiento@rlpharma.com.co), WhatsApp Fijo 318 837 1830 ext. 1007, buzón físico y formulario web cifrado." }
    ]
  },
  {
    id: "mod3", icon: "🔍", color: "#1e40af",
    title: "Debida Diligencia de Contrapartes",
    subtitle: "El Embudo de Vinculación — Filtrando el Riesgo",
    duration: 18,
    description: "Aprende el proceso de verificación obligatoria de clientes, proveedores y empleados antes de vincularlos.",
    slides: [
      {
        title: "Nadie Entra Sin Pasar el Filtro",
        content: "Nadie ingresa a nuestro ecosistema comercial o laboral sin atravesar la barrera de la Debida Diligencia. Conocer a nuestra contraparte no es opcional, es la ley. El proceso tiene 3 filtros secuenciales: (1) Cruce en Listas Restrictivas OFAC/ONU/Interpol — coincidencia = bloqueo automático, (2) Identificación del Beneficiario Final (RUB — Ley 2195), (3) Origen de Fondos y Ética — validación financiera y cero conflictos de interés.",
        keyPoints: ["Aplica a: clientes, proveedores, empleados, socios y contratistas", "Filtro 1 OFAC/ONU: coincidencia = BLOQUEO AUTOMÁTICO E INMEDIATO", "Inactividad >1 año: debe pasar el embudo nuevamente"]
      },
      {
        title: "Tres Niveles de Diligencia",
        content: "Simplificada (Riesgo Bajo): clientes habituales y proveedores estándar. Requiere RUT, Cámara de Comercio (<30 días) y cruce en listas. Estándar (Riesgo Medio): contratistas recurrentes y sector público básico. Agrega cuestionario ético, declaración de origen de fondos y revisión financiera. Intensificada (Riesgo Alto): contratos >500 SMLMV, PEPs, activos virtuales, zonas de riesgo. Requiere auditoría de medios, análisis societario y aprobación expresa de Gerencia.",
        keyPoints: ["Bajo: aprobación inmediata con documentos básicos", "Medio: requiere aprobación del Área + Oficial de Cumplimiento", "Alto: SOLO aprobación expresa de Gerencia General — indelegable"]
      },
      {
        title: "Radar PEP: Personas Expuestas Políticamente",
        content: "Un PEP (Persona Expuesta Políticamente) no está prohibida, pero exige el máximo nivel de alerta y control (Decreto 830). Son personas que manejan recursos públicos o tienen poder público destacado. IMPORTANTE: la calidad de PEP se extiende a cónyuges, familiares hasta 2° grado de consanguinidad y asociados comerciales con participación >5%. Se mantiene hasta 2 años después de dejar el cargo.",
        keyPoints: ["La condición PEP persiste 2 años después de dejar el cargo", "Se extiende a familiares (2° grado) y socios con >5% participación", "PROTOCOLO OBLIGATORIO: aprobación indelegable de Gerencia General"]
      },
      {
        title: "Líneas Rojas Operativas Absolutas",
        content: "Estas son nuestras líneas rojas NO negociables: EFECTIVO — límite estricto de $2.000.000 COP. Prohibido recibir o pagar sumas superiores; toda transacción mayor debe estar bancarizada. ACTIVOS VIRTUALES — cero exposición directa; RLpharma no realiza operaciones con criptoactivos salvo autorización expresa, escrita y excepcional de la Gerencia General. JURISDICCIONES — restricción total para operar en paraísos fiscales y zonas GAFI sin debida diligencia intensificada previa.",
        keyPoints: ["Efectivo máximo: $2.000.000 COP — todo exceso debe ser bancarizado", "Criptoactivos: PROHIBIDO sin autorización expresa escrita de Gerencia", "Paraísos fiscales/GAFI: restricción total sin DDI previa"]
      }
    ],
    quiz: [
      { id: "q3_1", text: "¿Cuál es el límite máximo de transacciones en efectivo en RLpharma?", options: ["$5.000.000 COP", "$10.000.000 COP", "$2.000.000 COP", "Sin límite si hay factura soporte"], correct: 2, explanation: "El límite estricto es $2.000.000 COP. Toda transacción mayor DEBE estar bancarizada para garantizar su trazabilidad. Este es un límite NO negociable." },
      { id: "q3_2", text: "¿Por cuánto tiempo se mantiene la condición de PEP después de que la persona deja su cargo?", options: ["6 meses", "1 año", "2 años", "La condición desaparece inmediatamente al dejar el cargo"], correct: 2, explanation: "La calidad de PEP se mantiene durante 2 años después de dejar el cargo. Durante ese período sigue siendo obligatoria la Debida Diligencia Intensificada." },
      { id: "q3_3", text: "Un proveedor nuevo (1 mes de existencia) solicita pago de $45M a una cuenta bancaria personal. ¿Qué se debe hacer?", options: ["Pagar rápido para no retrasar el servicio", "Dividir el pago en transferencias menores para evitar alertas", "Detener el pago y reportar al Oficial de Cumplimiento", "Pagar en efectivo para mayor agilidad"], correct: 2, explanation: "Empresa nueva + cuenta personal = señales de alerta de LA. Ante señales de alerta se debe PAUSAR y reportar. Un proveedor legítimo entiende la verificación. Dividir pagos (smurfing) es un DELITO." }
    ]
  },
  {
    id: "mod4", icon: "🚨", color: "#b91c1c",
    title: "Señales de Alerta y Canal Ético",
    subtitle: "El Radar Operativo — Detección y Reporte",
    duration: 15,
    description: "Identifica las señales de alerta de LA/FT/FPADM y usa correctamente el Canal Ético de RLpharma.",
    slides: [
      {
        title: "Las 4 Señales de Alerta Clave",
        content: "OPACIDAD: el tercero evade entregar información, elude identificar al Beneficiario Final o aporta datos falsos. INCONSISTENCIA FINANCIERA: operaciones que no corresponden al perfil económico, ingresos injustificados o uso de cuentas offshore. FRACCIONAMIENTO: transacciones divididas artificialmente para evadir controles (ej. el límite de efectivo) — delito llamado 'Smurfing'. PRESIÓN INDEBIDA: exigencia de comisiones no pactadas, intermediarios innecesarios o solicitudes de pagos urgentes sin soporte.",
        keyPoints: ["Opacidad: niega información o identifica al Beneficiario Final falsamente", "Fraccionamiento (Smurfing): dividir pagos para evadir límites = DELITO", "Presión indebida: comisiones extrañas, intermediarios misteriosos"]
      },
      {
        title: "De la Alerta al Reporte: ROS y AROS",
        content: "FLUJO: Empleado detecta señal → Reporta al Oficial de Cumplimiento → OC analiza → Si hay riesgo: envía ROS (Reporte de Operación Sospechosa) a la UIAF vía SIREL en máximo 15 días hábiles. Si trimestre limpio: envía AROS (Ausencia de ROS) dentro de los 10 primeros días del mes siguiente. ALERTA ROJA — TIPPING OFF: es un delito penal alertar al investigado que está bajo investigación.",
        keyPoints: ["ROS: máximo 15 días hábiles desde la detección de la alerta", "AROS: dentro de los 10 primeros días del mes siguiente al trimestre", "Tipping Off (alertar al investigado) = DELITO PENAL GRAVE"]
      },
      {
        title: "El Canal Ético: Tu Escudo Personal",
        content: "El Canal Ético tiene tres pilares fundamentales: PILAR 1 — 100% CONFIDENCIAL: acceso técnico exclusivo del Oficial de Cumplimiento y Revisoría Fiscal. Nadie más lee tu reporte. PILAR 2 — OPCIÓN DE ANONIMATO: tu identidad no es requisito para iniciar una investigación. PILAR 3 — CERO REPRESALIAS: cualquier intento de retaliación laboral por reportar es falta gravísima y causal inmediata de despido y denuncia penal.",
        keyPoints: ["Correo: oficialcumplimiento@rlpharma.com.co", "WhatsApp Fijo: 318 837 1830 Ext. 1007", "Investigación se cierra en máximo 30 días hábiles"]
      }
    ],
    quiz: [
      { id: "q4_1", text: "¿Cuál es el plazo máximo para reportar un ROS (Reporte de Operación Sospechosa) a la UIAF?", options: ["Dentro de los 10 primeros días del mes siguiente", "Máximo 30 días hábiles desde la detección", "Máximo 15 días hábiles desde la detección", "No tiene plazo, se reporta cuando sea posible"], correct: 2, explanation: "La Ley 526 exige reportar el ROS a más tardar dentro de los 15 días hábiles siguientes a la detección. Los 10 días del mes siguiente son para el AROS (ausencia de reportes)." },
      { id: "q4_2", text: "¿Qué es el 'Tipping Off' y qué consecuencias tiene?", options: ["Es una buena práctica de transparencia con el cliente investigado", "Es alertar al investigado que está bajo investigación — es un DELITO PENAL", "Es el procedimiento oficial para cerrar un caso de Canal Ético", "Es un reporte opcional al Oficial de Cumplimiento"], correct: 1, explanation: "El Tipping Off es alertar a la contraparte que está siendo investigada o reportada a la UIAF. Es un delito penal que puede comprometer toda la investigación." },
      { id: "q4_3", text: "Si ves a tu jefe entregando un sobre con dinero a un inspector del gobierno, ¿qué debes hacer?", options: ["Confrontar al jefe exigiendo que devuelva el dinero inmediatamente", "Quedarte callado para proteger tu empleo y no meterte en problemas", "Reportar anónimamente por el Canal Ético", "Llamar directamente a las autoridades sin informar al Oficial de Cumplimiento"], correct: 2, explanation: "El Canal Ético es la vía correcta y segura. Garantiza confidencialidad y la ley prohíbe represalias. Confrontar podría destruir evidencia o generar riesgos personales graves." }
    ]
  },
  {
    id: "mod5", icon: "⚖️", color: "#92400e",
    title: "PTEE: Ética, Regalos y Consecuencias",
    subtitle: "Control de Regalos · Conflictos de Interés · Régimen Sancionatorio",
    duration: 14,
    description: "Comprende las reglas sobre regalos, conflictos de interés y el impacto real de incumplir el SAGRILAFT y PTEE.",
    slides: [
      {
        title: "Semáforo de Regalos y Beneficios",
        content: "VERDE — Permitido con Registro: regalos corporativos con logo de RLpharma, cortesías de bajo perfil. Límite: menor a 1 SMLMV anual por persona. Requiere aviso al Oficial de Cumplimiento. AMARILLO — Requiere Aprobación (Gerencia General): donaciones a entidades sin ánimo de lucro (tope <5 SMLMV acumulado anual), patrocinios y viáticos excepcionales. ROJO — Estrictamente Prohibido: regalos en dinero en efectivo, cheques o criptomonedas; regalos a funcionarios durante procesos de licitación; pagos de facilitación burocráticos.",
        keyPoints: ["Límite máximo de regalos permitidos: <1 SMLMV anual por persona", "PROHIBICIÓN ABSOLUTA: efectivo, cheques, cripto como regalo", "Regalos a funcionarios en licitación: SIEMPRE prohibido"]
      },
      {
        title: "Árbol de Decisión: Conflictos de Interés",
        content: "Ante un posible conflicto de interés: ¿El proveedor/cliente es familiar hasta 2° grado o eres accionista de su empresa? → SÍ → ¿Tienes poder para decidir o influir en la contratación? → SÍ → ALERTA ROJA: ¡DECLARA EL CONFLICTO INMEDIATAMENTE! La Regla de Oro: usa el Anexo 1 para declarar el conflicto al Oficial de Cumplimiento. Abstente de intervenir en la decisión. La transparencia protege tu empleo.",
        keyPoints: ["Declarar con Anexo 1 al Oficial de Cumplimiento de inmediato", "Abstención TOTAL en la decisión vinculada al conflicto", "Ocultar un conflicto = falta gravísima + despido sin indemnización"]
      },
      {
        title: "El Costo Real de la Infracción",
        content: "Incumplir el SAGRILAFT y PTEE NO es solo un error administrativo; es una amenaza a la existencia de la compañía y a tu libertad personal. SANCIONES INTERNAS — Faltas leves: amonestaciones y suspensiones. Faltas graves/gravísimas (soborno, omitir controles): despido con justa causa sin indemnización. SANCIONES PENALES — Lavado de Activos: 10 a 30 años de prisión + multas hasta 50.000 SMLMV. Financiación del Terrorismo: 13 a 22 años. Omisión de denuncia: responsabilidad penal directa. CUSTODIA — toda información se conserva 10 años.",
        keyPoints: ["Lavado de Activos: hasta 30 años de prisión + 50.000 SMLMV de multa", "Omisión de denuncia (callar): responsabilidad penal directa", "Toda evidencia conservada 10 años por exigencia legal"]
      }
    ],
    quiz: [
      { id: "q5_1", text: "En una cena, un cliente te ofrece un reloj valorado en 2 SMLMV como agradecimiento. ¿Qué debes hacer?", options: ["Aceptarlo; es parte de la cultura de cerrar negocios", "Rechazarlo educadamente citando el Código de Ética y el límite de 1 SMLMV", "Aceptarlo pero no reportarlo para evitar problemas administrativos", "Aceptarlo y declararlo como ingreso personal en tu declaración de renta"], correct: 1, explanation: "El límite es 1 SMLMV anual. Un regalo de 2 SMLMV supera el límite y debe rechazarse. Al citar la política se elimina el conflicto personal. Aceptar y ocultar es una falta grave." },
      { id: "q5_2", text: "¿Cuáles son las sanciones penales por Lavado de Activos en Colombia?", options: ["Solo multa económica sin pena privativa de libertad", "5 a 8 años de prisión", "10 a 30 años de prisión más multas hasta 50.000 SMLMV", "Suspensión de licencias comerciales únicamente"], correct: 2, explanation: "El Lavado de Activos tiene penas de 10 a 30 años de prisión más multas hasta 50.000 SMLMV. No es un error administrativo; es una amenaza directa a la libertad." },
      { id: "q5_3", text: "¿Qué está ESTRICTAMENTE PROHIBIDO dar o recibir como regalo en RLpharma?", options: ["Regalos corporativos con logo de RLpharma menores a 1 SMLMV", "Cortesías de bajo perfil registradas ante el Oficial de Cumplimiento", "Dinero en efectivo, cheques o criptomonedas como regalo", "Agendas o lapiceros con el logo de la empresa"], correct: 2, explanation: "Los regalos en dinero en efectivo, cheques o criptomonedas están ESTRICTAMENTE PROHIBIDOS. También está prohibido dar regalos a funcionarios durante licitaciones y realizar pagos de facilitación." }
    ]
  }
];
const PASSING_SCORE = 80;

/* ══════════════════════════════════════════════════════════════
   UTILITY COMPONENTS
══════════════════════════════════════════════════════════════ */
const Shield = ({ size = 32, color = "#0055a5" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 2L3 7v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7L12 2z" fill={color} opacity="0.15" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M9 12l2 2 4-4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ProgressRing = ({ pct, size = 60, stroke = 5, color = "#0055a5" }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(0,51,102,0.1)" strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.8s ease" }}/>
    </svg>
  );
};

const TimerDisplay = ({ seconds }) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return <span style={{ fontFamily: "monospace", letterSpacing: 2 }}>{m}:{s}</span>;
};

/* ══════════════════════════════════════════════════════════════

/* ══════════════════════════════════════════════════════════════
   LOGIN VIEW
══════════════════════════════════════════════════════════════ */
const LoginView = ({ onLogin }) => {
  const [name, setName] = useState("");
  const [cargo, setCargo] = useState("");

  return (
    <div className="lms-root" style={{ minHeight: "100vh", background: "linear-gradient(135deg, #eef2f7 0%, #dce8f7 50%, #eef2f7 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 20% 20%, rgba(0,85,165,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(0,51,102,0.1) 0%, transparent 50%)", pointerEvents: "none" }}/>
      <div style={{ position: "absolute", top: -100, right: -100, width: 400, height: 400, borderRadius: "50%", border: "1px solid rgba(0,51,102,0.06)", pointerEvents: "none" }}/>

      <div className="animate-fadeUp" style={{ width: "100%", maxWidth: 460 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <img src={IMG_LOGO} alt="RLpharma Distribuciones" style={{ height: 72, objectFit: "contain", marginBottom: 12 }}/>
          <div className="lms-title" style={{ fontSize: 16, color: "rgba(0,51,102,0.55)", letterSpacing: 2, textTransform: "uppercase" }}>Plataforma de Capacitación</div>
          <div style={{ width: 60, height: 2, background: "linear-gradient(90deg, transparent, #0055a5, transparent)", margin: "10px auto 0" }}/>
        </div>

        <div className="glass-card" style={{ borderRadius: 16, padding: 40 }}>
          <h2 className="lms-title" style={{ fontSize: 24, fontWeight: 700, color: "#003366", marginBottom: 6 }}>Acceso al Sistema</h2>
          <p style={{ color: "rgba(0,51,102,0.5)", fontSize: 13, marginBottom: 32 }}>Ingresa tus datos para comenzar tu capacitación</p>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontSize: 11, color: "#0055a5", letterSpacing: 2, textTransform: "uppercase", marginBottom: 8, fontWeight: 600 }}>Nombre Completo</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Ej. María García López"
              onKeyDown={e => e.key === "Enter" && cargo && name.trim() && onLogin(name.trim(), cargo.trim())}
              style={{ width: "100%", padding: "12px 16px", background: "rgba(0,51,102,0.06)", border: "1px solid rgba(0,51,102,0.2)", borderRadius: 8, color: "#003366", fontSize: 14, outline: "none", boxSizing: "border-box" }}
              onFocus={e => e.target.style.borderColor = "#003366"} onBlur={e => e.target.style.borderColor = "rgba(0,51,102,0.2)"}/>
          </div>
          <div style={{ marginBottom: 32 }}>
            <label style={{ display: "block", fontSize: 11, color: "#0055a5", letterSpacing: 2, textTransform: "uppercase", marginBottom: 8, fontWeight: 600 }}>Cargo</label>
            <input value={cargo} onChange={e => setCargo(e.target.value)} placeholder="Ej. Asesor Comercial"
              onKeyDown={e => e.key === "Enter" && cargo && name.trim() && onLogin(name.trim(), cargo.trim())}
              style={{ width: "100%", padding: "12px 16px", background: "rgba(0,51,102,0.06)", border: "1px solid rgba(0,51,102,0.2)", borderRadius: 8, color: "#003366", fontSize: 14, outline: "none", boxSizing: "border-box" }}
              onFocus={e => e.target.style.borderColor = "#003366"} onBlur={e => e.target.style.borderColor = "rgba(0,51,102,0.2)"}/>
          </div>
          <button onClick={() => name.trim() && onLogin(name.trim(), cargo.trim())} disabled={!name.trim()}
            className="shimmer-btn" style={{ width: "100%", padding: 14, borderRadius: 10, border: "none", color: "#ffffff", fontSize: 14, fontWeight: 700, cursor: name.trim() ? "pointer" : "not-allowed", opacity: name.trim() ? 1 : 0.5, letterSpacing: 1, textTransform: "uppercase" }}>
            Acceder ›
          </button>
        </div>
        <p style={{ textAlign: "center", marginTop: 20, fontSize: 12, color: "rgba(0,51,102,0.25)" }}>Sistema de Cumplimiento Integral · RLpharma S.A.S.</p>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   HOME VIEW — Selector de Área de Capacitación
══════════════════════════════════════════════════════════════ */
const HomeView = ({ employee, onSelectArea, onLogout }) => {
  const AREAS = [
    {
      id: "sagrilaft",
      icon: "🛡️",
      title: "SAGRILAFT & PTEE",
      subtitle: "Cumplimiento Normativo",
      description: "Prevención Lavado de Activos, Financiación del Terrorismo, Transparencia y Ética Empresarial.",
      tags: ["Capacitación", "Documentación", "Videos"],
      color: "#003366",
      gradient: "linear-gradient(135deg, #003366 0%, #0055a5 100%)",
      available: true,
    },
    {
      id: "sst",
      icon: "⛑️",
      title: "Seguridad y Salud",
      subtitle: "SST — Sistema de Gestión",
      description: "Normas de seguridad, prevención de riesgos laborales y protocolos de salud en el trabajo.",
      tags: ["Próximamente"],
      color: "#15803d",
      gradient: "linear-gradient(135deg, #15803d 0%, #16a34a 100%)",
      available: false,
    },
    {
      id: "talento",
      icon: "👥",
      title: "Talento Humano",
      subtitle: "Gestión del Personal",
      description: "Políticas internas, clima organizacional, beneficios, reglamento interno y desarrollo profesional.",
      tags: ["Próximamente"],
      color: "#92400e",
      gradient: "linear-gradient(135deg, #92400e 0%, #b45309 100%)",
      available: false,
    },
  ];

  return (
    <div className="lms-root" style={{ minHeight: "100vh", background: "#eef2f7", color: "#003366" }}>
      {/* Header */}
      <div style={{ background: "#003366", padding: "0 28px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64, position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 12px rgba(0,51,102,0.2)" }}>
        <img src={IMG_LOGO} alt="RLpharma" style={{ height: 36, objectFit: "contain", filter: "brightness(0) invert(1)" }}/>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ color: "#ffffff", fontWeight: 600, fontSize: 13 }}>{employee.name}</div>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>{employee.cargo}</div>
          </div>
          <button onClick={onLogout} style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, padding: "6px 14px", color: "rgba(255,255,255,0.8)", cursor: "pointer", fontSize: 12 }}>
            Salir
          </button>
        </div>
      </div>

      {/* Hero */}
      <div style={{ background: "linear-gradient(135deg, #003366 0%, #0055a5 100%)", padding: "48px 28px 56px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 10% 50%, rgba(255,255,255,0.05) 0%, transparent 50%), radial-gradient(circle at 90% 30%, rgba(255,255,255,0.04) 0%, transparent 50%)", pointerEvents: "none" }}/>
        <div className="animate-fadeUp">
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>Bienvenido/a</div>
          <h1 className="lms-title" style={{ fontSize: 38, fontWeight: 800, color: "#ffffff", marginBottom: 8, letterSpacing: 0.5 }}>
            Hola, {employee.name.split(" ")[0]} 👋
          </h1>
          <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 15, maxWidth: 500, margin: "0 auto" }}>
            Selecciona el área de capacitación que te han solicitado completar.
          </p>
        </div>
      </div>

      {/* Wave separator */}
      <div style={{ height: 40, background: "#003366", borderRadius: "0 0 50% 50% / 0 0 40px 40px", marginTop: -2 }}/>

      {/* Cards */}
      <div style={{ maxWidth: 960, margin: "-20px auto 0", padding: "0 24px 48px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
          {AREAS.map((area, i) => (
            <div key={area.id} className={area.available ? "module-card" : ""} onClick={() => area.available && onSelectArea(area.id)}
              style={{ background: "#ffffff", borderRadius: 20, overflow: "hidden", border: "1px solid rgba(0,51,102,0.08)", boxShadow: "0 2px 16px rgba(0,51,102,0.06)", opacity: area.available ? 1 : 0.7, cursor: area.available ? "pointer" : "default", animation: `fadeSlideUp 0.5s ease ${i * 0.1}s both` }}>
              {/* Card header */}
              <div style={{ background: area.gradient, padding: "28px 24px 24px", position: "relative" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>{area.icon}</div>
                <div className="lms-title" style={{ fontSize: 22, fontWeight: 800, color: "#ffffff", lineHeight: 1.1 }}>{area.title}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 4, letterSpacing: 0.5 }}>{area.subtitle}</div>
                {!area.available && (
                  <div style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "4px 10px", fontSize: 10, color: "#ffffff", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
                    Próximamente
                  </div>
                )}
              </div>
              {/* Card body */}
              <div style={{ padding: "20px 24px 24px" }}>
                <p style={{ color: "rgba(0,51,102,0.7)", fontSize: 13, lineHeight: 1.7, marginBottom: 16 }}>{area.description}</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {area.tags.map(tag => (
                    <span key={tag} style={{ background: area.available ? `rgba(0,51,102,0.08)` : "rgba(0,51,102,0.04)", border: `1px solid rgba(0,51,102,0.1)`, borderRadius: 20, padding: "3px 10px", fontSize: 11, color: area.available ? area.color : "rgba(0,51,102,0.35)", fontWeight: 600 }}>
                      {tag}
                    </span>
                  ))}
                </div>
                {area.available && (
                  <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 6, color: area.color, fontSize: 13, fontWeight: 600 }}>
                    Ingresar <span style={{ fontSize: 16 }}>›</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   SAGRILAFT HUB VIEW — 3 tabs: Videos | Material | Capacitación
══════════════════════════════════════════════════════════════ */
const SagrilaftHubView = ({ employee, modules, progress, timeSpent, onStartModule, onCertificate, onBack,
  showAI, setShowAI, aiMessages, aiInput, setAiInput, onAskAI, isAILoading }) => {
  const [tab, setTab] = useState("videos");
  // El video debe verse antes de acceder a capacitación
  const [videoVisto, setVideoVisto] = useState(() => {
    try { return localStorage.getItem("rl_video_visto") === "1"; } catch { return false; }
  });

  const marcarVideoVisto = () => {
    setVideoVisto(true);
    try { localStorage.setItem("rl_video_visto", "1"); } catch {}
  };

  const handleTabClick = (id) => {
    if (id === "capacitacion" && !videoVisto) return; // bloqueado
    setTab(id);
  };

  const TABS = [
    { id: "videos",        label: "🎬 Videos" },
    { id: "documentacion", label: "📄 Material" },
    { id: "capacitacion",  label: "📚 Capacitación", bloqueado: !videoVisto },
  ];

  const passed = Object.values(progress).filter(p => p?.passed).length;
  const overallPct = Math.round((passed / modules.length) * 100);

  const getStatus = (mod) => {
    const p = progress[mod.id];
    if (!p) return "pending";
    return p.passed ? "passed" : "failed";
  };

  const statusConfig = {
    pending: { label: "Pendiente", color: "rgba(0,51,102,0.4)", bg: "rgba(0,51,102,0.06)", icon: "○" },
    passed:  { label: "Aprobado",  color: "#15803d", bg: "rgba(21,128,61,0.08)", icon: "✓" },
    failed:  { label: "Reintentar", color: "#92400e", bg: "rgba(146,64,14,0.08)", icon: "↺" }
  };

  return (
    <div className="lms-root" style={{ minHeight: "100vh", background: "#eef2f7", color: "#003366" }}>
      {/* Header */}
      <div style={{ background: "#003366", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64, position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 12px rgba(0,51,102,0.2)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, padding: "6px 14px", color: "#ffffff", cursor: "pointer", fontSize: 13 }}>‹ Inicio</button>
          <img src={IMG_LOGO} alt="RLpharma" style={{ height: 32, objectFit: "contain", filter: "brightness(0) invert(1)" }}/>
          <div style={{ width: 1, height: 28, background: "rgba(255,255,255,0.2)" }}/>
          <div className="lms-title" style={{ color: "#ffffff", fontSize: 15, fontWeight: 700, letterSpacing: 0.5 }}>🛡️ SAGRILAFT & PTEE</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ color: "#ffffff", fontWeight: 600, fontSize: 12 }}>{employee.name}</div>
            <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 11 }}>{employee.cargo}</div>
          </div>
          <button onClick={() => setShowAI(!showAI)} style={{ background: showAI ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, padding: "6px 14px", color: "#ffffff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
            🤖 IA Tutor
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ background: "#ffffff", borderBottom: "1px solid rgba(0,51,102,0.1)", padding: "0 24px", display: "flex", gap: 4 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => handleTabClick(t.id)}
            title={t.bloqueado ? "⚠️ Debes ver el video primero para acceder a la capacitación" : ""}
            style={{
              padding: "14px 20px", background: "none", border: "none",
              borderBottom: tab === t.id ? "3px solid #0055a5" : "3px solid transparent",
              color: t.bloqueado ? "rgba(0,51,102,0.25)" : tab === t.id ? "#003366" : "rgba(0,51,102,0.45)",
              fontSize: 13, fontWeight: tab === t.id ? 700 : 400,
              cursor: t.bloqueado ? "not-allowed" : "pointer",
              transition: "all 0.2s", whiteSpace: "nowrap",
              display: "flex", alignItems: "center", gap: 6,
            }}>
            {t.label}
            {t.bloqueado && <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", borderRadius: 10, padding: "1px 7px", fontWeight: 700 }}>Ver video primero</span>}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ display: "flex", maxWidth: 1200, margin: "0 auto", padding: 24, gap: 24 }}>
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* ─── TAB: VIDEOS ─── */}
          {tab === "videos" && (
            <div className="animate-fadeUp">
              <h2 className="lms-title" style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: "#003366" }}>🎬 Videos de Capacitación</h2>
              <p style={{ color: "rgba(0,51,102,0.5)", fontSize: 13, marginBottom: 24 }}>Debes ver el video completo para desbloquear los módulos de capacitación.</p>

              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {/* Main video */}
                <div className="glass-card" style={{ borderRadius: 20, overflow: "hidden", border: `2px solid ${videoVisto ? "#15803d" : "#0055a5"}` }}>

                  {/* Banner obligatorio */}
                  {!videoVisto && (
                    <div style={{ background: "linear-gradient(135deg,#003366,#0055a5)", padding: "12px 20px", display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 20 }}>⚠️</span>
                      <div>
                        <div style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>Visualización obligatoria</div>
                        <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>Ver este video es requerido para acceder a los módulos de capacitación.</div>
                      </div>
                    </div>
                  )}
                  {videoVisto && (
                    <div style={{ background: "linear-gradient(135deg,#15803d,#16a34a)", padding: "10px 20px", display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 18 }}>✅</span>
                      <div style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>Video completado — Capacitación desbloqueada</div>
                    </div>
                  )}

                  <div style={{ background: "#000", position: "relative", paddingTop: "56.25%" }}>
                    <video
                      controls
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
                      onEnded={marcarVideoVisto}
                      poster=""
                    >
                      <source src="./videos/fortaleza.mp4" type="video/mp4"/>
                      <source src="/videos/fortaleza.mp4" type="video/mp4"/>
                      Tu navegador no soporta video HTML5.
                    </video>
                  </div>
                  <div style={{ padding: "20px 24px" }}>
                    <div style={{ display: "inline-block", background: "rgba(0,51,102,0.06)", borderRadius: 20, padding: "3px 10px", fontSize: 11, color: "#0055a5", fontWeight: 600, marginBottom: 10 }}>🎬 Video Institucional · Obligatorio</div>
                    <h3 className="lms-title" style={{ fontSize: 20, fontWeight: 800, color: "#003366", marginBottom: 6 }}>La Fortaleza de Cumplimiento</h3>
                    <p style={{ color: "rgba(0,51,102,0.6)", fontSize: 13, lineHeight: 1.7, marginBottom: 16 }}>
                      Video introductorio al sistema de cumplimiento de RLpharma. Comprende la importancia del SAGRILAFT y el PTEE para proteger la empresa y a todos sus colaboradores.
                    </p>

                    {!videoVisto && (
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <div style={{ flex: 1, padding: "12px 16px", background: "rgba(0,85,165,0.06)", borderRadius: 10, border: "1px solid rgba(0,85,165,0.12)" }}>
                          <div style={{ fontSize: 12, color: "#0055a5", fontWeight: 600, marginBottom: 4 }}>⚙️ Configuración local</div>
                          <div style={{ fontSize: 12, color: "rgba(0,51,102,0.6)", lineHeight: 1.6 }}>
                            Copia <code style={{ background: "rgba(0,51,102,0.06)", padding: "1px 6px", borderRadius: 4, fontSize: 11 }}>Video_Fortaleza_de_Cumplimiento.mp4</code> a <code style={{ background: "rgba(0,51,102,0.06)", padding: "1px 6px", borderRadius: 4, fontSize: 11 }}>public/videos/fortaleza.mp4</code>
                          </div>
                        </div>
                        <button onClick={marcarVideoVisto} style={{ background: "rgba(0,51,102,0.06)", border: "1px solid rgba(0,51,102,0.15)", borderRadius: 10, padding: "12px 20px", color: "#003366", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                          ✓ Marcar como visto (desarrollo)
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Proximos videos */}
                {[
                  { title: "Señales de Alerta y Cómo Reportarlas", duration: "~8 min", desc: "Casos prácticos de señales de alerta de lavado de activos y el proceso de reporte." },
                  { title: "El Canal Ético: Tu Escudo Personal", duration: "~6 min", desc: "Cómo usar el canal ético, garantías de confidencialidad y protección al denunciante." },
                  { title: "Debida Diligencia en la Práctica", duration: "~10 min", desc: "Simulación paso a paso del proceso de vinculación de una contraparte nueva." },
                ].map((v, i) => (
                  <div key={i} className="glass-card" style={{ borderRadius: 16, padding: "18px 22px", display: "flex", alignItems: "center", gap: 18, border: "1px solid rgba(0,51,102,0.08)", opacity: 0.6 }}>
                    <div style={{ width: 56, height: 56, borderRadius: 12, background: "rgba(0,51,102,0.06)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>🎬</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#003366", marginBottom: 3 }}>{v.title}</div>
                      <div style={{ fontSize: 12, color: "rgba(0,51,102,0.5)", marginBottom: 6 }}>{v.duration}</div>
                      <div style={{ fontSize: 12, color: "rgba(0,51,102,0.55)", lineHeight: 1.5 }}>{v.desc}</div>
                    </div>
                    <div style={{ background: "rgba(0,51,102,0.06)", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "rgba(0,51,102,0.4)", fontWeight: 600, whiteSpace: "nowrap" }}>Próximamente</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── TAB: MATERIAL / DOCUMENTACIÓN ─── */}
          {tab === "documentacion" && (
            <div className="animate-fadeUp">
              <DocumentacionTab />
            </div>
          )}

          {/* ─── TAB: CAPACITACIÓN ─── */}
          {tab === "capacitacion" && (
            <div className="animate-fadeUp">
              {/* Progress banner */}
              <div className="glass-card" style={{ borderRadius: 16, padding: "24px 28px", marginBottom: 24, background: "linear-gradient(135deg, #003366 0%, #0055a5 100%)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>Progreso General</div>
                    <div className="lms-title" style={{ fontSize: 28, fontWeight: 800, color: "#ffffff" }}>{passed}/{modules.length} módulos completados</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{ textAlign: "center" }}>
                      <ProgressRing pct={overallPct} size={72} stroke={6} color="#ffffff"/>
                      <div style={{ marginTop: -44, fontSize: 16, fontWeight: 800, color: "#ffffff" }}>{overallPct}%</div>
                    </div>
                    {overallPct === 100 && (
                      <button onClick={onCertificate} style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "10px 20px", color: "#ffffff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                        🎓 Ver Constancia
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ height: 6, background: "rgba(255,255,255,0.15)", borderRadius: 3, marginTop: 20 }}>
                  <div style={{ height: "100%", width: `${overallPct}%`, background: "rgba(255,255,255,0.8)", borderRadius: 3, transition: "width 0.8s ease" }}/>
                </div>
              </div>

              {/* Module grid */}
              <h2 className="lms-title" style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: "#003366", letterSpacing: 0.5, textTransform: "uppercase" }}>Módulos de Capacitación</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {modules.map((mod, i) => {
                  const st = getStatus(mod);
                  const cfg = statusConfig[st];
                  const p = progress[mod.id];
                  return (
                    <div key={mod.id} className="module-card glass-card" onClick={() => onStartModule(mod)}
                      style={{ borderRadius: 14, padding: "18px 22px", display: "flex", alignItems: "center", gap: 18, border: `1px solid rgba(0,51,102,0.08)` }}>
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: `${mod.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
                        {mod.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 3 }}>
                          <span style={{ fontSize: 11, color: "rgba(0,51,102,0.4)", fontWeight: 600 }}>Módulo {i + 1}</span>
                          <span style={{ fontSize: 11, background: cfg.bg, color: cfg.color, borderRadius: 20, padding: "2px 10px", fontWeight: 700 }}>{cfg.icon} {cfg.label}</span>
                          {p?.score && <span style={{ fontSize: 11, color: "rgba(0,51,102,0.4)" }}>{p.score}%</span>}
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#003366", marginBottom: 2 }}>{mod.title}</div>
                        <div style={{ fontSize: 12, color: "rgba(0,51,102,0.5)" }}>{mod.subtitle} · {mod.duration} min</div>
                      </div>
                      <div style={{ color: "#0055a5", fontSize: 20, flexShrink: 0 }}>›</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>

        {/* AI Sidebar */}
        {showAI && <AITutor messages={aiMessages} input={aiInput} setInput={setAiInput} onSend={onAskAI} isLoading={isAILoading} onClose={() => setShowAI(false)} />}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   DOCUMENT CARD — sub-component for documentation tab
══════════════════════════════════════════════════════════════ */
const DocumentCard = ({ title, subtitle, type, typeColor, icon, image, description }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="glass-card" style={{ borderRadius: 16, overflow: "hidden", border: "1px solid rgba(0,51,102,0.1)", cursor: "pointer" }} onClick={() => setExpanded(!expanded)}>
      {/* Thumbnail */}
      <div style={{ position: "relative", height: 160, overflow: "hidden", background: "#f0f4f8" }}>
        <img src={image} alt={title} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s", transform: expanded ? "scale(1.02)" : "scale(1)" }}/>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent 40%, rgba(0,51,102,0.3) 100%)" }}/>
        <div style={{ position: "absolute", top: 10, left: 10, background: `${typeColor}ee`, borderRadius: 20, padding: "3px 10px", fontSize: 10, color: "#ffffff", fontWeight: 700, letterSpacing: 0.5 }}>{type}</div>
        <div style={{ position: "absolute", bottom: 10, right: 10, background: "rgba(255,255,255,0.9)", borderRadius: 20, padding: "4px 10px", fontSize: 11, color: "#003366", fontWeight: 600 }}>
          {expanded ? "Ver menos ↑" : "Ver completo ↓"}
        </div>
      </div>
      {/* Info */}
      <div style={{ padding: "14px 16px" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#003366", marginBottom: 2 }}>{icon} {title}</div>
        <div style={{ fontSize: 11, color: "rgba(0,51,102,0.45)", marginBottom: 8 }}>{subtitle}</div>
        <p style={{ fontSize: 12, color: "rgba(0,51,102,0.6)", lineHeight: 1.6 }}>{description}</p>
      </div>
      {/* Expanded full image */}
      {expanded && (
        <div style={{ padding: "0 0 16px" }}>
          <img src={image} alt={title} style={{ width: "100%", objectFit: "contain", maxHeight: 600, background: "#f8f9fb" }}/>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   AI TUTOR
══════════════════════════════════════════════════════════════ */
const AITutor = ({ messages, input, setInput, onSend, isLoading, onClose }) => {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, isLoading]);

  return (
    <div className="animate-fadeRight glass-card" style={{ width: 340, borderRadius: 16, display: "flex", flexDirection: "column", height: "calc(100vh - 108px)", position: "sticky", top: 84, overflow: "hidden" }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#003366" }}>🤖 IA Tutor de Cumplimiento</div>
          <div style={{ fontSize: 11, color: "#0055a5" }}>Pregunta sobre SAGRILAFT & PTEE</div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(0,51,102,0.45)", cursor: "pointer", fontSize: 18 }}>×</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🛡️</div>
            <div style={{ fontSize: 13, color: "rgba(0,51,102,0.55)", lineHeight: 1.6 }}>Soy tu asistente de cumplimiento. Pregúntame sobre SAGRILAFT, PTEE, debida diligencia, canal ético o cualquier duda sobre las políticas de RLpharma.</div>
            {["¿Qué es el Tipping Off?", "¿Cuándo debo rechazar un regalo?", "¿Qué son los ROS y AROS?"].map((q, i) => (
              <button key={i} onClick={() => { setInput(q); }} style={{ display: "block", width: "100%", background: "rgba(0,51,102,0.06)", border: "1px solid rgba(0,85,165,0.2)", borderRadius: 8, padding: "8px 12px", color: "rgba(0,51,102,0.75)", fontSize: 12, cursor: "pointer", marginTop: 8, textAlign: "left" }}>
                {q} ›
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div className={m.role === "user" ? "ai-bubble-user" : "ai-bubble-bot"} style={{ maxWidth: "85%", padding: "10px 14px", borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px", fontSize: 13, lineHeight: 1.6, color: m.role === "user" ? "#ffffff" : "#003366" }}>
              {m.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className="ai-bubble-bot" style={{ padding: "12px 16px", borderRadius: "16px 16px 16px 4px", display: "flex", gap: 4, alignItems: "center" }}>
              {[0,1,2].map(i => <div key={i} className="typing-dot" style={{ width: 6, height: 6, borderRadius: "50%", background: "#0055a5" }}/>)}
            </div>
          </div>
        )}
        <div ref={endRef}/>
      </div>

      <div style={{ padding: 12, borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && !isLoading && input.trim() && onSend()} placeholder="Escribe tu pregunta..." style={{ flex: 1, background: "rgba(0,51,102,0.06)", border: "1px solid rgba(0,51,102,0.2)", borderRadius: 8, padding: "10px 12px", color: "#003366", fontSize: 13, outline: "none" }}/>
        <button onClick={onSend} disabled={isLoading || !input.trim()} style={{ background: input.trim() && !isLoading ? "#0055a5" : "rgba(0,51,102,0.12)", border: "none", borderRadius: 8, padding: "10px 14px", color: "#003366", cursor: "pointer", fontSize: 16, transition: "background 0.2s" }}>›</button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   MODULE CONTENT VIEW
══════════════════════════════════════════════════════════════ */
const ModuleView = ({ module, slideIndex, totalTime, onNext, onBack, onGoQuiz }) => {
  const slide = module.slides[slideIndex];
  const totalSlides = module.slides.length;
  const pct = Math.round(((slideIndex + 1) / totalSlides) * 100);

  return (
    <div className="lms-root" style={{ minHeight: "100vh", background: "#eef2f7", color: "#003366", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ background: "#003366", borderBottom: "1px solid rgba(255,255,255,0.1)", padding: "0 24px", display: "flex", alignItems: "center", gap: 16, height: 60, flexShrink: 0, backdropFilter: "blur(12px)" }}>
        <button onClick={onBack} style={{ background: "rgba(0,51,102,0.06)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "6px 14px", color: "rgba(255,255,255,0.85)", cursor: "pointer", fontSize: 13 }}>‹ Volver</button>
        <div style={{ fontSize: 20, marginLeft: 4 }}>{module.icon}</div>
        <div style={{ flex: 1 }}>
          <div className="lms-title" style={{ fontSize: 15, fontWeight: 700, color: "#ffffff" }}>{module.title}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>Diapositiva {slideIndex + 1} de {totalSlides}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "rgba(255,255,255,0.7)", fontSize: 13 }}>
          <span>⏱</span>
          <TimerDisplay seconds={totalTime}/>
        </div>
      </div>

      {/* Progress */}
      <div style={{ height: 3, background: "rgba(0,51,102,0.06)" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg, ${module.color}, ${module.color}cc)`, transition: "width 0.4s ease" }}/>
      </div>

      {/* Slide */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
        <div className="animate-scale" style={{ maxWidth: 720, width: "100%" }}>
          <div className="glass-card" style={{ borderRadius: 20, padding: "40px 48px", borderColor: `${module.color}25` }}>
            {/* Slide header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
              <div style={{ width: 4, height: 40, background: module.color, borderRadius: 2 }}/>
              <div>
                <div style={{ fontSize: 11, color: module.color, letterSpacing: 2, textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Módulo {MODULES.findIndex(m => m.id === module.id) + 1} · Diapositiva {slideIndex + 1}</div>
                <h2 className="lms-title" style={{ fontSize: 26, fontWeight: 800, color: "#003366", letterSpacing: 0.3 }}>{slide.title}</h2>
              </div>
            </div>

            {/* Content */}
            <p style={{ color: "rgba(0,51,102,0.8)", lineHeight: 1.8, fontSize: 15, marginBottom: 32 }}>{slide.content}</p>

            {/* Key points */}
            <div style={{ background: `${module.color}08`, border: `1px solid ${module.color}20`, borderRadius: 12, padding: "20px 24px" }}>
              <div style={{ fontSize: 11, color: module.color, letterSpacing: 2, textTransform: "uppercase", fontWeight: 600, marginBottom: 14 }}>PUNTOS CLAVE</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {slide.keyPoints.map((kp, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <span style={{ color: module.color, fontSize: 12, marginTop: 3, flexShrink: 0 }}>◆</span>
                    <span style={{ color: "#003366", fontSize: 14, lineHeight: 1.5 }}>{kp}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Nav */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 32 }}>
              <div style={{ display: "flex", gap: 6 }}>
                {module.slides.map((_, i) => (
                  <div key={i} style={{ width: i === slideIndex ? 20 : 6, height: 6, borderRadius: 3, background: i === slideIndex ? module.color : "rgba(0,51,102,0.15)", transition: "all 0.3s" }}/>
                ))}
              </div>
              <button onClick={onNext} className="shimmer-btn" style={{ padding: "12px 32px", borderRadius: 10, border: "none", color: "#003366", fontWeight: 700, fontSize: 14, cursor: "pointer", letterSpacing: 0.5 }}>
                {slideIndex < totalSlides - 1 ? "Siguiente ›" : "Ir a la Evaluación →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   QUIZ VIEW
══════════════════════════════════════════════════════════════ */
const QuizView = ({ module, state, onAnswer, onSubmit, onRetry, onBack, onNextModule }) => {
  const { answers, currentQ, submitted, score, passed } = state;
  const quiz = module.quiz;

  if (submitted) {
    return (
      <div className="lms-root" style={{ minHeight: "100vh", background: "#eef2f7", color: "#003366", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div className="animate-scale glass-card" style={{ maxWidth: 540, width: "100%", borderRadius: 20, padding: 40, textAlign: "center", borderColor: passed ? "rgba(21,128,61,0.2)" : "rgba(240,165,0,0.2)" }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>{passed ? "🏆" : "📚"}</div>
          <h2 className="lms-title" style={{ fontSize: 32, fontWeight: 800, marginBottom: 8, color: passed ? "#15803d" : "#92400e" }}>{passed ? "¡Módulo Aprobado!" : "Necesitas Repasar"}</h2>
          <p style={{ color: "rgba(0,51,102,0.55)", marginBottom: 28, fontSize: 14 }}>{module.title}</p>

          <div style={{ position: "relative", display: "inline-block", marginBottom: 28 }}>
            <ProgressRing pct={score} size={120} stroke={8} color={passed ? "#15803d" : "#92400e"}/>
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <span className="lms-title" style={{ fontSize: 28, fontWeight: 900, color: "#003366" }}>{score}%</span>
              <span style={{ fontSize: 11, color: "rgba(0,51,102,0.45)" }}>Puntaje</span>
            </div>
          </div>

          <div style={{ background: "#ffffff", borderRadius: 10, padding: "16px 20px", marginBottom: 28 }}>
            <div style={{ fontSize: 13, color: "rgba(0,51,102,0.65)" }}>
              Respuestas correctas: <strong style={{ color: "#003366" }}>{quiz.filter((q, i) => answers[i] === q.correct).length}/{quiz.length}</strong> · 
              Mínimo aprobatorio: <strong style={{ color: passed ? "#15803d" : "#92400e" }}>{PASSING_SCORE}%</strong>
            </div>
          </div>

          {/* Review answers */}
          <div style={{ textAlign: "left", marginBottom: 28 }}>
            {quiz.map((q, i) => {
              const correct = answers[i] === q.correct;
              return (
                <div key={i} style={{ background: correct ? "rgba(34,197,94,0.06)" : "rgba(239,68,68,0.06)", border: `1px solid ${correct ? "rgba(21,128,61,0.2)" : "rgba(239,68,68,0.2)"}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: correct ? "#15803d" : "#b91c1c", fontWeight: 600, marginBottom: 6 }}>{correct ? "✓ Correcto" : "✗ Incorrecto"}</div>
                  <div style={{ fontSize: 13, color: "#003366", marginBottom: 6 }}>{q.text}</div>
                  {!correct && <div style={{ fontSize: 12, color: "rgba(0,51,102,0.55)" }}>Tu respuesta: {q.options[answers[i]] || "Sin respuesta"} · Correcta: {q.options[q.correct]}</div>}
                  <div style={{ fontSize: 12, color: "rgba(0,51,102,0.5)", marginTop: 6, paddingTop: 6, borderTop: "1px solid rgba(255,255,255,0.06)" }}>{q.explanation}</div>
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            {!passed && <button onClick={onRetry} style={{ flex: 1, padding: "12px", borderRadius: 10, background: "rgba(240,165,0,0.15)", border: "1px solid rgba(240,165,0,0.3)", color: "#92400e", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>↺ Repasar Módulo</button>}
            <button onClick={onBack} className={passed ? "shimmer-btn" : ""} style={{ flex: 1, padding: "12px", borderRadius: 10, border: passed ? "none" : "1px solid rgba(255,255,255,0.15)", background: passed ? undefined : "rgba(0,51,102,0.06)", color: "#003366", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
              {passed ? "→ Ver Dashboard" : "← Dashboard"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const q = quiz[currentQ];
  const progressPct = Math.round((currentQ / quiz.length) * 100);

  return (
    <div className="lms-root" style={{ minHeight: "100vh", background: "#eef2f7", color: "#003366", display: "flex", flexDirection: "column" }}>
      <div style={{ background: "#003366", borderBottom: "1px solid rgba(255,255,255,0.1)", padding: "0 24px", display: "flex", alignItems: "center", gap: 16, height: 60, backdropFilter: "blur(12px)" }}>
        <button onClick={onBack} style={{ background: "rgba(0,51,102,0.06)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "6px 14px", color: "rgba(255,255,255,0.85)", cursor: "pointer", fontSize: 13 }}>‹ Salir</button>
        <div style={{ fontSize: 20 }}>{module.icon}</div>
        <div style={{ flex: 1 }}>
          <div className="lms-title" style={{ fontSize: 15, fontWeight: 700, color: "#ffffff" }}>Evaluación: {module.title}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>Pregunta {currentQ + 1} de {quiz.length}</div>
        </div>
        <div style={{ fontSize: 13, color: "#92400e" }}>Mínimo: {PASSING_SCORE}%</div>
      </div>
      <div style={{ height: 3, background: "rgba(0,51,102,0.06)" }}>
        <div style={{ height: "100%", width: `${progressPct}%`, background: `linear-gradient(90deg, ${module.color}, ${module.color}cc)`, transition: "width 0.4s" }}/>
      </div>

      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
        <div className="animate-scale" style={{ maxWidth: 680, width: "100%" }}>
          {/* Progress dots */}
          <div style={{ display: "flex", gap: 6, marginBottom: 24, justifyContent: "center" }}>
            {quiz.map((_, i) => (
              <div key={i} style={{ width: i === currentQ ? 24 : 8, height: 8, borderRadius: 4, background: i < currentQ ? "#15803d" : i === currentQ ? module.color : "rgba(0,51,102,0.12)", transition: "all 0.3s" }}/>
            ))}
          </div>

          {/* Question */}
          <div className="glass-card" style={{ borderRadius: 16, padding: "32px 36px", marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: module.color, letterSpacing: 2, textTransform: "uppercase", fontWeight: 600, marginBottom: 16 }}>PREGUNTA {currentQ + 1}</div>
            <p style={{ fontSize: 18, fontWeight: 600, color: "#003366", lineHeight: 1.6, marginBottom: 28 }}>{q.text}</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {q.options.map((opt, i) => (
                <button key={i} onClick={() => onAnswer(currentQ, i)} className={`option-btn ${answers[currentQ] === i ? "selected" : ""}`}
                  style={{ textAlign: "left", padding: "14px 18px", borderRadius: 10, border: `2px solid ${answers[currentQ] === i ? module.color : "rgba(0,51,102,0.1)"}`, background: answers[currentQ] === i ? `${module.color}12` : "rgba(0,51,102,0.04)", color: "#003366", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", border: `2px solid ${answers[currentQ] === i ? module.color : "rgba(0,51,102,0.15)"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: answers[currentQ] === i ? module.color : "rgba(0,51,102,0.45)", flexShrink: 0 }}>
                    {String.fromCharCode(65 + i)}
                  </div>
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            {currentQ < quiz.length - 1 ? (
              <button onClick={() => onAnswer(currentQ + 1, undefined)} disabled={answers[currentQ] === undefined}
                style={{ padding: "12px 28px", borderRadius: 10, background: answers[currentQ] !== undefined ? module.color : "rgba(0,51,102,0.12)", border: "none", color: "#003366", fontWeight: 700, fontSize: 14, cursor: answers[currentQ] !== undefined ? "pointer" : "not-allowed", opacity: answers[currentQ] !== undefined ? 1 : 0.5, transition: "all 0.2s" }}>
                Siguiente ›
              </button>
            ) : (
              <button onClick={onSubmit} disabled={Object.keys(answers).length < quiz.length}
                className="shimmer-btn" style={{ padding: "12px 28px", borderRadius: 10, border: "none", color: "#003366", fontWeight: 700, fontSize: 14, cursor: Object.keys(answers).length >= quiz.length ? "pointer" : "not-allowed", opacity: Object.keys(answers).length >= quiz.length ? 1 : 0.5 }}>
                Enviar Evaluación ✓
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   CERTIFICATE VIEW
══════════════════════════════════════════════════════════════ */
const CertificateView = ({ employee, progress, modules, onBack }) => {
  const now = new Date();
  const dateStr = now.toLocaleDateString("es-CO", { year: "numeric", month: "long", day: "numeric" });
  const avgScore = Math.round(Object.values(progress).filter(p => p?.passed).reduce((a, p) => a + p.score, 0) / modules.length);
  const totalTime = Object.values(progress).reduce((a, p) => a + (p?.timeSpent || 0), 0);

  return (
    <div className="lms-root" style={{ minHeight: "100vh", background: "#eef2f7", color: "#003366", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div className="no-print" style={{ marginBottom: 24, display: "flex", gap: 12 }}>
        <button onClick={onBack} style={{ background: "rgba(0,51,102,0.1)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "8px 20px", color: "#003366", cursor: "pointer", fontSize: 14 }}>‹ Volver</button>
        <button onClick={() => window.print()} style={{ background: "#0055a5", border: "none", borderRadius: 8, padding: "8px 20px", color: "#003366", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>🖨️ Imprimir Constancia</button>
      </div>

      <div className="cert-print" style={{ maxWidth: 760, width: "100%", background: "linear-gradient(135deg, #f0f6ff 0%, #dce8f7 50%, #f0f6ff 100%)", border: "2px solid #0055a5", borderRadius: 20, padding: "48px 56px", position: "relative", overflow: "hidden" }}>
        {/* Decorative elements */}
        <div style={{ position: "absolute", top: -60, right: -60, width: 200, height: 200, borderRadius: "50%", border: "1px solid rgba(0,85,165,0.1)", pointerEvents: "none" }}/>
        <div style={{ position: "absolute", bottom: -40, left: -40, width: 160, height: 160, borderRadius: "50%", border: "1px solid rgba(240,165,0,0.1)", pointerEvents: "none" }}/>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <Shield size={40} color="#0055a5"/>
            <div>
              <div className="lms-title" style={{ fontSize: 26, fontWeight: 800, color: "#003366" }}>RLpharma S.A.S.</div>
              <div style={{ fontSize: 11, color: "#0055a5", letterSpacing: 3, textTransform: "uppercase" }}>Distribuciones · Sector Salud</div>
            </div>
          </div>
          <div style={{ width: 80, height: 2, background: "linear-gradient(90deg, transparent, #0055a5, transparent)", margin: "0 auto 20px" }}/>
          <h1 className="lms-title" style={{ fontSize: 30, fontWeight: 900, color: "#92400e", letterSpacing: 1, textTransform: "uppercase" }}>Constancia de Capacitación</h1>
          <p style={{ color: "rgba(0,51,102,0.55)", fontSize: 13, marginTop: 6 }}>Sistema Integrado de Cumplimiento — SAGRILAFT & PTEE 2025</p>
        </div>

        {/* Content */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <p style={{ color: "rgba(0,51,102,0.65)", fontSize: 15, marginBottom: 16 }}>Se certifica que</p>
          <div className="lms-title" style={{ fontSize: 36, fontWeight: 900, color: "#003366", letterSpacing: 0.5, marginBottom: 8 }}>{employee.name}</div>
          <div style={{ fontSize: 15, color: "#0055a5", marginBottom: 24 }}>{employee.cargo}</div>
          <p style={{ color: "rgba(0,51,102,0.65)", fontSize: 14, lineHeight: 1.8, maxWidth: 500, margin: "0 auto" }}>
            Ha completado satisfactoriamente el Programa de Capacitación en el Sistema de Autocontrol y Gestión del Riesgo de LA/FT/FPADM (SAGRILAFT) y el Programa de Transparencia y Ética Empresarial (PTEE) de RLpharma S.A.S.
          </p>
        </div>

        {/* Stats grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 32 }}>
          {[
            { label: "Módulos Aprobados", value: `${modules.length}/${modules.length}`, color: "#15803d" },
            { label: "Promedio General", value: `${avgScore}%`, color: "#0055a5" },
            { label: "Tiempo de Estudio", value: `${Math.floor(totalTime / 60)} min`, color: "#1e40af" },
            { label: "Fecha de Emisión", value: now.toLocaleDateString("es-CO"), color: "#92400e" }
          ].map((s, i) => (
            <div key={i} style={{ background: "#ffffff", border: "1px solid rgba(0,51,102,0.1)", borderRadius: 10, padding: "14px 12px", textAlign: "center" }}>
              <div className="lms-title" style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 10, color: "rgba(0,51,102,0.45)", marginTop: 4, textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Modules list */}
        <div style={{ background: "#fafcff", borderRadius: 10, padding: "16px 20px", marginBottom: 32 }}>
          <div style={{ fontSize: 11, color: "rgba(0,51,102,0.45)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 14 }}>Módulos Completados</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {modules.map(m => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: "#15803d", fontSize: 12 }}>✓</span>
                <span style={{ fontSize: 13, color: "rgba(0,51,102,0.75)" }}>{m.title}</span>
                <span style={{ fontSize: 12, color: "#15803d", marginLeft: "auto" }}>{progress[m.id]?.score}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 20 }}>
          <div>
            <div style={{ fontSize: 12, color: "rgba(0,51,102,0.45)" }}>Oficial de Cumplimiento</div>
            <div style={{ fontSize: 14, color: "#003366", fontWeight: 600, marginTop: 4 }}>oficialcumplimiento@rlpharma.com.co</div>
            <div style={{ fontSize: 12, color: "#0055a5" }}>Ext. 1007</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: "rgba(0,51,102,0.45)", marginBottom: 4 }}>Fecha de emisión</div>
            <div style={{ fontSize: 13, color: "#003366" }}>{dateStr}</div>
            <div style={{ width: 120, height: 1, background: "#92400e", marginTop: 12 }}/>
            <div style={{ fontSize: 11, color: "rgba(0,51,102,0.3)", marginTop: 4 }}>Firma digital del sistema</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   MAIN APP
══════════════════════════════════════════════════════════════ */
export default function App() {
  const [view, setView] = useState("login");
  const [employee, setEmployee] = useState(null);
  const [progress, setProgress] = useState({});
  const [timeSpent, setTimeSpent] = useState({});
  const [currentModule, setCurrentModule] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [quizState, setQuizState] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState("");
  const [isAILoading, setIsAILoading] = useState(false);

  const timerRef = useRef(null);
  const activeModuleRef = useRef(null);

  /* —— TIMER —— */
  const startTimer = useCallback((modId) => {
    activeModuleRef.current = modId;
    timerRef.current = setInterval(() => {
      setTimeSpent(prev => ({ ...prev, [modId]: (prev[modId] || 0) + 1 }));
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  useEffect(() => () => stopTimer(), [stopTimer]);

  /* —— STORAGE —— */
  const saveProgress = async (empId, modId, data) => {
    try { await window.storage.set(`prog:${empId}:${modId}`, JSON.stringify(data)); } catch (_) {}
  };

  const loadProgress = async (empId) => {
    const result = {};
    for (const m of MODULES) {
      try {
        const r = await window.storage.get(`prog:${empId}:${m.id}`);
        if (r) result[m.id] = JSON.parse(r.value);
      } catch (_) {}
    }
    return result;
  };

  /* —— LOGIN → HOME —— */
  const handleLogin = async (name, cargo) => {
    const emp = { id: `${name.toLowerCase().replace(/\s+/g, "_")}_${Date.now()}`, name, cargo };
    setEmployee(emp);
    const saved = await loadProgress(emp.id);
    setProgress(saved);
    setView("home");
  };

  /* —— AREA SELECTION —— */
  const handleSelectArea = (areaId) => {
    if (areaId === "sagrilaft") setView("sagrilaft_hub");
    // SST and Talento coming soon — no action yet
  };

  /* —— START MODULE —— */
  const handleStartModule = (mod) => {
    stopTimer();
    setCurrentModule(mod);
    setSlideIndex(0);
    startTimer(mod.id);
    setView("module");
  };

  /* —— SLIDE NAV —— */
  const handleNextSlide = () => {
    if (slideIndex < currentModule.slides.length - 1) {
      setSlideIndex(i => i + 1);
    } else {
      stopTimer();
      setQuizState({ answers: {}, currentQ: 0, submitted: false });
      setView("quiz");
    }
  };

  /* —— QUIZ —— */
  const handleQuizAnswer = (qIdx, aIdx) => {
    setQuizState(prev => {
      const newAnswers = { ...prev.answers, [qIdx]: aIdx };
      const nextQ = qIdx + 1 < currentModule.quiz.length ? qIdx + 1 : qIdx;
      return { ...prev, answers: newAnswers, currentQ: nextQ };
    });
  };

  const handleQuizSubmit = async () => {
    const quiz = currentModule.quiz;
    const correct = quiz.filter((q, i) => quizState.answers[i] === q.correct).length;
    const score = Math.round((correct / quiz.length) * 100);
    const passed = score >= PASSING_SCORE;
    const data = { passed, score, timeSpent: timeSpent[currentModule.id] || 0, completedAt: new Date().toISOString() };
    await saveProgress(employee.id, currentModule.id, data);
    setProgress(prev => ({ ...prev, [currentModule.id]: data }));
    setQuizState(prev => ({ ...prev, submitted: true, score, passed }));
  };

  const handleQuizRetry = () => {
    setSlideIndex(0);
    startTimer(currentModule.id);
    setView("module");
  };

  /* —— AI TUTOR —— */
  const handleAskAI = async () => {
    if (!aiInput.trim() || isAILoading) return;
    const msg = aiInput.trim();
    setAiInput("");
    setAiMessages(prev => [...prev, { role: "user", content: msg }]);
    setIsAILoading(true);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514", max_tokens: 1000,
          system: `Eres el Asistente Virtual de Cumplimiento de RLpharma S.A.S. Ayudas a los empleados a entender SAGRILAFT y PTEE. Sé conciso, claro y siempre en español. Datos clave: apetito de riesgo CERO, límite efectivo $2M COP, límite regalos 1 SMLMV/año, Canal Ético: oficialcumplimiento@rlpharma.com.co / Ext. 1007, ROS plazo: 15 días hábiles, AROS: primeros 10 días del mes siguiente. Si hay dudas serias, siempre remite al Canal Ético.`,
          messages: [...aiMessages.map(m => ({ role: m.role, content: m.content })), { role: "user", content: msg }]
        })
      });
      const data = await res.json();
      setAiMessages(prev => [...prev, { role: "assistant", content: data.content?.[0]?.text || "Error al obtener respuesta." }]);
    } catch (_) {
      setAiMessages(prev => [...prev, { role: "assistant", content: "Error de conexión. Por favor intenta de nuevo." }]);
    }
    setIsAILoading(false);
  };

  /* —— RENDER —— */
  if (view === "login") return <LoginView onLogin={handleLogin}/>;

  if (view === "home") return (
    <HomeView employee={employee} onSelectArea={handleSelectArea} onLogout={() => { setEmployee(null); setView("login"); }}/>
  );

  if (view === "sagrilaft_hub") return (
    <SagrilaftHubView
      employee={employee} modules={MODULES} progress={progress} timeSpent={timeSpent}
      onStartModule={handleStartModule} onCertificate={() => setView("certificate")}
      onBack={() => setView("home")}
      showAI={showAI} setShowAI={setShowAI} aiMessages={aiMessages} aiInput={aiInput}
      setAiInput={setAiInput} onAskAI={handleAskAI} isAILoading={isAILoading}
    />
  );

  if (view === "module") return (
    <ModuleView module={currentModule} slideIndex={slideIndex} totalTime={timeSpent[currentModule?.id] || 0}
      onNext={handleNextSlide} onBack={() => { stopTimer(); setView("sagrilaft_hub"); }}/>
  );

  if (view === "quiz") return (
    <QuizView module={currentModule} state={quizState}
      onAnswer={handleQuizAnswer} onSubmit={handleQuizSubmit}
      onRetry={handleQuizRetry} onBack={() => setView("sagrilaft_hub")}/>
  );

  if (view === "certificate") return (
    <CertificateView employee={employee} progress={progress} modules={MODULES} onBack={() => setView("sagrilaft_hub")}/>
  );

  return null;
}
